import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, Layers, Loader2, MessageSquare, Star, X } from "lucide-react";
import { fetchLibraryMaterials } from "../lib/library";
import MaterialCarousel from "../components/MaterialCarousel";

const Tagline = ({ value }) => {
  if (!value) return null;
  const isPopular = /popular|best/i.test(value);
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-sm"
      style={{
        background: isPopular ? "#FFB500" : "#1C1917",
        color: isPopular ? "#1C1917" : "#FFB500",
      }}
    >
      {isPopular && <Star size={10} fill="currentColor" />} {value}
    </span>
  );
};

const MaterialCard = ({ m, onOpen }) => {
  const images = m.images && m.images.length > 0
    ? m.images
    : (m.image_url ? [{ url: m.image_url, caption: null }] : []);
  return (
    <button
      type="button"
      onClick={() => onOpen(m)}
      className="group text-left flex flex-col rounded-sm overflow-hidden transition-all hover:-translate-y-1"
      style={{ background: "#FFFFFF", border: "1px solid var(--ups-border)", boxShadow: "4px 4px 0 transparent" }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "6px 6px 0 #FFB500")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "4px 4px 0 transparent")}
      data-testid={`library-card-${m.slug}`}
    >
      <div
        className="relative overflow-hidden"
        style={{ aspectRatio: "1 / 1", background: "var(--ups-muted)" }}
      >
        {images.length > 0 ? (
          <MaterialCarousel
            images={images}
            intervalMs={4000}
            showDots={images.length > 1}
            showCaption={images.length > 1}
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
            ariaLabel={m.label}
            pauseOnHover={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Layers size={36} className="text-stone-300" />
          </div>
        )}
        {m.tagline && (
          <div className="absolute top-2 left-2 z-10">
            <Tagline value={m.tagline} />
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="font-display text-base font-black tracking-tight leading-tight">{m.label}</div>
        {m.feels_like && (
          <div className="text-xs text-[color:var(--ups-subtext)] leading-snug">
            <span className="font-bold text-[color:var(--ups-ink)] uppercase tracking-[0.1em] text-[9px]">Feels like</span><br />
            {m.feels_like}
          </div>
        )}
        {m.best_for && (
          <div className="text-xs text-[color:var(--ups-subtext)] leading-snug">
            <span className="font-bold text-[color:var(--ups-ink)] uppercase tracking-[0.1em] text-[9px]">Best for</span><br />
            {m.best_for}
          </div>
        )}
        <div className="mt-auto pt-2 flex items-center justify-end border-t" style={{ borderColor: "var(--ups-border)" }}>
          <span className="text-[11px] font-bold uppercase tracking-[0.12em] flex items-center gap-1 group-hover:gap-2 transition-all">
            Details <ArrowRight size={12} />
          </span>
        </div>
      </div>
    </button>
  );
};

const MaterialDetail = ({ m, onClose, onQuote }) => {
  const images = m.images && m.images.length > 0
    ? m.images
    : (m.image_url ? [{ url: m.image_url, caption: null }] : []);
  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
      style={{ background: "rgba(28, 25, 23, 0.7)" }}
      onClick={onClose}
      data-testid="library-detail-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-3xl max-h-[92vh] overflow-auto rounded-t-md sm:rounded-sm"
        style={{ border: "2px solid var(--ups-ink)", boxShadow: "8px 8px 0 #FFB500" }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-5">
          <div
            className="sm:col-span-2 aspect-square sm:aspect-auto sm:min-h-[420px]"
            style={{ background: "var(--ups-muted)" }}
          >
            {images.length > 0 ? (
              <MaterialCarousel
                images={images}
                intervalMs={4000}
                showDots={images.length > 1}
                showCaption={images.length > 1}
                className="w-full h-full"
                imgClassName="w-full h-full object-cover"
                ariaLabel={m.label}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Layers size={48} className="text-stone-300" />
              </div>
            )}
          </div>
          <div className="sm:col-span-3 p-6 flex flex-col gap-3 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {m.tagline && <Tagline value={m.tagline} />}
                <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-2 leading-tight">{m.label}</h2>
                <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">
                  {(m.product_categories || []).join(" · ")}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-[color:var(--ups-muted)] rounded-sm shrink-0"
                aria-label="Close"
                data-testid="library-detail-close"
              >
                <X size={20} />
              </button>
            </div>
            {m.feels_like && (
              <div className="text-sm mt-1">
                <div className="overline">Feels like</div>
                <div className="text-stone-700">{m.feels_like}</div>
              </div>
            )}
            {m.best_for && (
              <div className="text-sm">
                <div className="overline">Best for</div>
                <div className="text-stone-700">{m.best_for}</div>
              </div>
            )}
            {m.spec_pairs && m.spec_pairs.length > 0 && (
              <div className="border rounded-sm overflow-hidden mt-2" style={{ borderColor: "var(--ups-border)" }}>
                {m.spec_pairs.map((sp, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-2 text-sm"
                    style={{ borderBottom: i < m.spec_pairs.length - 1 ? "1px solid var(--ups-border)" : "none" }}
                  >
                    <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[color:var(--ups-subtext)] bg-[color:var(--ups-muted)]">
                      {sp.key}
                    </div>
                    <div className="px-3 py-2 font-mono">{sp.value}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 flex items-center justify-end flex-wrap gap-2">
              <button
                onClick={() => onQuote(m)}
                className="btn-brutal px-4 py-2.5 text-[12px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-2"
                data-testid="library-detail-quote"
              >
                <MessageSquare size={14} /> Get a quote
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MaterialLibrary = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.title = "Material Library | Roswell Print Center";
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchLibraryMaterials()
      .then((d) => { if (active) setData(d); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const visibleItems = useMemo(() => {
    if (!data) return [];
    if (cat === "All") return data.items;
    return data.items.filter((m) => (m.product_categories || []).includes(cat));
  }, [data, cat]);

  const categories = useMemo(() => {
    if (!data) return [];
    // Only show categories that actually have items, plus "All".
    const present = new Set();
    data.items.forEach((m) => (m.product_categories || []).forEach((c) => present.add(c)));
    return ["All", ...data.categories.filter((c) => present.has(c))];
  }, [data]);

  const goToQuote = (m) => {
    setSelected(null);
    // Land the customer on the email gate with the product pre-selected
    // (the chatbot can read this from sessionStorage later).
    try { sessionStorage.setItem("library_intent", JSON.stringify({ slug: m.slug, label: m.label })); } catch { /* ignore */ }
    navigate("/");
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ups-paper)" }} data-testid="material-library-page">
      <header className="sticky top-0 z-30 border-b" style={{ background: "var(--ups-ink)", borderColor: "var(--ups-ink)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-3 text-white">
            <img src="/site-logo.webp" alt="Roswell Printhub" className="h-10 w-auto rounded-sm" />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-sm font-bold">Material Library</span>
              <span className="text-[10px] font-mono opacity-70">public · educational</span>
            </div>
          </a>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-xs font-bold uppercase tracking-[0.15em] flex items-center gap-1.5 px-3 py-1.5 text-white hover:bg-white/10 rounded-sm transition"
            data-testid="library-goto-quote"
          >
            <MessageSquare size={14} /> Get a Quote
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <div className="overline flex items-center gap-1.5"><BookOpen size={11} /> Library</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black leading-none tracking-tight mt-2">
            What is it,<br />and what's it for?
          </h1>
          <p className="text-stone-600 mt-3 max-w-2xl text-sm sm:text-base">
            Plain-English photos of every paper, vinyl and substrate we print on. Tap a card to see what it feels like, what it's best for, and what it costs. Not sure?{" "}
            <button onClick={() => navigate("/")} className="underline font-bold text-[color:var(--ups-ink)]" data-testid="library-cta-chat">chat with us →</button>
          </p>
        </div>

        {/* Category pills */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="library-category-pills">
          {categories.map((c) => {
            const active = cat === c;
            return (
              <button
                key={c}
                onClick={() => setCat(c)}
                className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm border-2 transition"
                style={{
                  background: active ? "var(--ups-ink)" : "transparent",
                  color: active ? "#FFB500" : "var(--ups-ink)",
                  borderColor: active ? "var(--ups-ink)" : "var(--ups-border)",
                }}
                data-testid={`library-pill-${c}`}
              >
                {c}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-stone-500"><Loader2 size={14} className="animate-spin" /> Loading materials…</div>
        ) : visibleItems.length === 0 ? (
          <div className="text-sm text-stone-500" data-testid="library-empty">
            No materials in this category yet. Check back soon, or{" "}
            <button onClick={() => navigate("/")} className="underline font-bold text-[color:var(--ups-ink)]">ask in chat</button>.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {visibleItems.map((m) => (
              <MaterialCard key={m.id} m={m} onOpen={setSelected} />
            ))}
          </div>
        )}
      </main>

      {selected && <MaterialDetail m={selected} onClose={() => setSelected(null)} onQuote={goToQuote} />}
    </div>
  );
};

export default MaterialLibrary;
